import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { zoneRegionEditorFeature } from '@states/zone-region-editor/zone-region-editor.feature';
import { ZoneRegionEditorActions } from '@states/zone-region-editor/zone-region-editor.actions';
import { ZoneRegionsActions } from '@states/zone-regions/zone-regions.actions';
import { ZoneRegionsService } from '../../services/zone-regions.service';
import { UtilsService } from '../../edge/utils.service';
import { LoggerService } from '../../services/logger.service';
import { ContextualLoggerService } from '../../services/contextual-logger.service';

const context = 'ZoneRegionEditorEffects';
const createLogger = (baseLogger: LoggerService) => new ContextualLoggerService(baseLogger, context);

export const startLoadingAllZoneRegions$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
  ) =>
    actions$.pipe(
      ofType(ZoneRegionEditorActions.startLoadingAllZoneRegions),
      withLatestFrom(
        store.select(zoneRegionEditorFeature.selectIsLoading),
        store.select(zoneRegionEditorFeature.selectIsLoaded),
      ),
      switchMap(([, isLoading, isLoaded]) => {
        if (isLoading || isLoaded) {
          return [];
        }
        return [ZoneRegionEditorActions.loadAllZoneRegions()];
      }),
    ),
  { functional: true },
);

export const loadAllZoneRegions$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    utilsService = inject(UtilsService),
    baseLogger = inject(LoggerService),
  ) => {
    const logger = createLogger(baseLogger);
    return actions$.pipe(
      ofType(ZoneRegionEditorActions.loadAllZoneRegions),
      switchMap(() =>
        zoneRegionsService.getAllZoneRegionsShallow().pipe(
          map((zoneRegions) =>
            ZoneRegionEditorActions.loadAllZoneRegionsSuccess({ zoneRegions: zoneRegions ?? [] }),
          ),
          catchError((response) => {
            logger.error(utilsService.errMessage(response));
            return of(ZoneRegionEditorActions.loadAllZoneRegionsFail());
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const syncZoneRegionsDeleteSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ZoneRegionsActions.deleteZoneRegionSuccess),
      map(({ id }) => ZoneRegionEditorActions.removeZoneRegion({ id })),
    ),
  { functional: true },
);
